import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { formatMoney, toMinor } from "@/lib/money";
import { LAYOUTS, pageSize, paginate } from "@/lib/tagLayouts";
import { useAuth, useCan } from "@/context/AuthContext";
import { NoAccess } from "@/components/NoAccess";
import { TagPages } from "@/components/tags/TagPages";
import { TagPreview } from "@/components/tags/TagPreview";
import { TagOptions } from "@/components/tags/TagOptions";
import { ProductPicker, SelectedList } from "@/components/tags/TagProductPicker";

const fefo = (p) => (p.batches || []).find((b) => !b.expired && b.qty > 0) || null;
const mk = (p) => ({ product: p, copies: 1, batchId: fefo(p)?.id || null });

function usePrintTags(printKey, L) {
  useEffect(() => () => document.body.classList.remove("printing-tags"), []);
  useEffect(() => {
    if (!printKey) return;
    let style = document.getElementById("receipt-page-size");
    if (!style) {
      style = document.createElement("style");
      style.id = "receipt-page-size";
      document.head.appendChild(style);
    }
    style.textContent = `@page { size: ${pageSize(L)}; margin: 0; }`;
    document.body.classList.add("printing-tags");
    const off = () => document.body.classList.remove("printing-tags");
    window.addEventListener("afterprint", off, { once: true });
    const id = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(id);
  }, [printKey]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function PriceTags() {
  const can = useCan();
  return can("products") ? <PriceTagsScreen /> : <NoAccess what="price tags" />;
}

function PriceTagsScreen() {
  const { session } = useAuth();
  const biz = session.business;
  const pharmacy = biz.business_type === "pharmacy";
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState([]);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [printKey, setPrintKey] = useState(0);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/tags/settings")])
      .then(([p, s]) => {
        setProducts(p.data.products);
        setSettings(s.data);
        const ids = (params.get("ids") || "").split(",").filter(Boolean);
        setSel(ids.map((id) => p.data.products.find((x) => x.id === id)).filter(Boolean).map(mk));
      })
      .catch((e) => toast.error(errMsg(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const L = LAYOUTS[settings?.layout || "a4_40"];
  usePrintTags(printKey, L);

  const tags = useMemo(
    () =>
      sel.flatMap((s) => {
        const p = s.product;
        const t = { name: p.name, barcode: p.barcode, priceText: formatMoney(toMinor(p.price, biz.decimals), biz.decimals, biz.currency), batch: (p.batches || []).find((b) => b.id === s.batchId) || null };
        return Array(s.copies).fill(t);
      }),
    [sel, biz]
  );

  if (!settings) return <div className="grid h-full place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));
  const add = (p) => setSel((xs) => (xs.some((x) => x.product.id === p.id) ? xs.map((x) => (x.product.id === p.id ? { ...x, copies: Math.min(500, x.copies + 1) } : x)) : [...xs, mk(p)]));
  const setCopies = (id, n) => setSel((xs) => xs.map((x) => (x.product.id === id ? { ...x, copies: Math.min(500, Math.max(1, n)) } : x)));
  const setBatch = (id, batchId) => setSel((xs) => xs.map((x) => (x.product.id === id ? { ...x, batchId } : x)));
  const remove = (id) => setSel((xs) => xs.filter((x) => x.product.id !== id));

  const save = async () => {
    setSaving(true);
    try {
      setSettings((await api.put("/tags/settings", settings)).data);
      toast.success("Tag settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const print = async () => {
    setBusy(true);
    try {
      const missing = sel.filter((s) => !s.product.barcode).map((s) => s.product.id);
      if (missing.length) {
        const { data } = await api.post("/tags/barcodes", { product_ids: missing });
        const codes = Object.fromEntries(data.products.map((p) => [p.id, p.barcode]));
        const patch = (p) => (codes[p.id] ? { ...p, barcode: codes[p.id] } : p);
        setSel((xs) => xs.map((x) => ({ ...x, product: patch(x.product) })));
        setProducts((ps) => ps.map(patch));
        toast.success(`Created ${missing.length} in-store barcode${missing.length > 1 ? "s" : ""}`, { description: data.products.map((p) => p.barcode).join(", ") });
      }
      setPrintKey(Date.now());
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const pages = paginate(L, tags, settings.start_position).length;
  return (
    <div className="h-full overflow-y-auto" data-testid="price-tags-page">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8">
        <div className="eyebrow">Labels · {session.branch.name} prices</div>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Price tags</h1>
        <p className="mt-1 text-sm text-slate-500">Pick products, set copies, check the preview, then print on A4 sticker sheets or a thermal label roll.</p>
        <div className="mt-6 grid gap-6 lg:grid-cols-[400px_1fr]">
          <div className="space-y-4">
            <ProductPicker products={products} onAdd={add} />
            <SelectedList sel={sel} pharmacy={pharmacy} setCopies={setCopies} setBatch={setBatch} remove={remove} />
            <TagOptions settings={settings} set={set} pharmacy={pharmacy} onSave={save} saving={saving} />
          </div>
          <div className="space-y-4 lg:sticky lg:top-0 lg:self-start">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-slate-950 p-3 pl-4 text-white">
              <div className="min-w-0 flex-1" data-testid="tags-summary">
                <div className="font-display text-lg font-bold"><span data-testid="tags-count">{tags.length}</span> tag{tags.length === 1 ? "" : "s"}</div>
                <div className="font-mono text-[10.5px] uppercase tracking-widest text-slate-400">
                  {pages} {L.kind === "a4" ? `A4 sheet${pages === 1 ? "" : "s"}` : "labels"} · page <span className="normal-case" data-testid="tags-page-size">{pageSize(L)}</span>
                </div>
              </div>
              <button onClick={print} disabled={!tags.length || busy} data-testid="tags-print-button" className="flex h-12 items-center gap-2 rounded-xl bg-amber-500 px-5 font-bold text-slate-950 transition-colors hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-400">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Print tags
              </button>
            </div>
            <TagPreview L={L} tags={tags} fields={settings.fields} bizName={biz.name} start={settings.start_position} />
          </div>
        </div>
      </div>
      {printKey > 0 &&
        createPortal(
          <div id="tag-print-root" data-testid="tag-print-root" data-page-size={pageSize(L)}>
            <TagPages L={L} tags={tags} start={settings.start_position} fields={settings.fields} bizName={biz.name} />
          </div>,
          document.body
        )}
    </div>
  );
}
